import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { format } from 'date-fns';
import { getGLTransactionById, GLTransaction } from '../firebase/glTransactionServices';

interface ReverseGLEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  date: Date;
  glTransactionId: string;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2
  }).format(amount);
};

const ReverseGLEntryDialog: React.FC<ReverseGLEntryDialogProps> = ({
  open,
  onClose,
  onConfirm,
  date,
  glTransactionId
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<GLTransaction | null>(null);

  useEffect(() => {
    if (open && glTransactionId) {
      fetchTransaction();
    }
  }, [open, glTransactionId]);

  const fetchTransaction = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const transactionData = await getGLTransactionById(glTransactionId);
      setTransaction(transactionData);
    } catch (err) {
      console.error('Error fetching GL transaction:', err);
      setError('Failed to load GL transaction details.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Error reversing GL transaction:', err);
      setError('Failed to reverse GL transaction.');
    } finally {
      setConfirming(false);
    }
  };

  // Calculate totals
  const totalDebits = transaction?.entries?.reduce((sum, entry) => sum + entry.debit, 0) || 0;
  const totalCredits = transaction?.entries?.reduce((sum, entry) => sum + entry.credit, 0) || 0;

  return (
    <Dialog open={open} onClose={!confirming ? onClose : undefined} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Reverse Daily Closing
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="h6" gutterBottom>
          Reverse Daily Closing for {date ? format(date, 'MMMM d, yyyy') : ''}
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          This action will reopen the day and reverse all GL entries. This cannot be undone.
        </Alert>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="subtitle1" gutterBottom>
          The following GL entries will be reversed:
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : transaction ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell align="right">Debit (RM)</TableCell>
                  <TableCell align="right">Credit (RM)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.entries.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell>{entry.accountNumber}</TableCell>
                    <TableCell>{entry.accountName}</TableCell>
                    <TableCell align="right">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                    <TableCell align="right">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</TableCell>
                  </TableRow>
                ))}
                
                {/* Totals row */}
                <TableRow sx={{ 
                  '& td': { 
                    fontWeight: 'bold',
                    borderTop: '2px solid rgba(224, 224, 224, 1)'
                  } 
                }}>
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell align="right">{formatCurrency(totalDebits)}</TableCell>
                  <TableCell align="right">{formatCurrency(totalCredits)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">No GL transaction data available.</Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={confirming}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="warning" 
          variant="contained"
          disabled={confirming || loading}
        >
          {confirming ? 'Reversing...' : 'Reverse Closing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReverseGLEntryDialog;
